(function () {
  const HIDDEN_PRICING_KEY = '_omalley_pricing';
  const HIDDEN_PCT_KEY = '_omalley_pct';
  const VISIBLE_PRICING_KEY = 'omalley_pricing';
  const VISIBLE_PCT_KEY = 'omalley_pct';
  const CART_ATTRIBUTE_KEY = 'omalley_line_pricing';
  const WHOLESALE_ATTRIBUTE_KEY = 'omalley_wholesale_customer';
  const DISCOUNT_APPLIED_KEY = 'omalley_wholesale_discount_applied';
  const WHOLESALE_VOLUME_MIN_QTYS = [20, 10, 5];

  function wholesaleBaseCode(percent) {
    const p = Math.round(Number(percent) || 15);
    if (p === 15) return 'OMALLEY-WHOLESALE';
    if (p === 50) return 'OMALLEY-WHOLESALE-50';
    return `OMALLEY-WHOLESALE-P${p}`;
  }

  function wholesaleVolumeCode(percent, minQty) {
    const p = Math.round(Number(percent) || 15);
    if (p === 15) return `OMALLEY-WV${minQty}`;
    if (p === 50) return `OMALLEY-WV${minQty}-50`;
    return `OMALLEY-WV${minQty}-P${p}`;
  }

  function allKnownWholesaleCodes() {
    const codes = new Set();
    for (const target of readPricingData()) {
      const p = Math.round(Number(target.wholesalePercent) || 15);
      codes.add(wholesaleBaseCode(p));
      for (const minQty of WHOLESALE_VOLUME_MIN_QTYS) {
        codes.add(wholesaleVolumeCode(p, minQty));
      }
    }
    const meta = document.querySelector('meta[name="omalley-cart-wholesale-tier"]');
    const tier = Math.round(Number(meta?.getAttribute('content') || 15));
    codes.add(wholesaleBaseCode(tier));
    for (const minQty of WHOLESALE_VOLUME_MIN_QTYS) {
      codes.add(wholesaleVolumeCode(tier, minQty));
    }
    return [...codes];
  }
  let syncing = false;
  let applyingDiscount = false;

  function readWholesaleCustomerFlag() {
    const meta = document.querySelector('meta[name="omalley-wholesale-customer"]');
    return meta?.getAttribute('content') === 'true';
  }

  function readWholesaleDiscountCode() {
    const meta = document.querySelector('meta[name="omalley-wholesale-discount-code"]');
    const code = meta?.getAttribute('content')?.trim();
    return code || wholesaleBaseCode(15);
  }

  function getCartWholesalePercents() {
    const percents = new Set();
    for (const target of readPricingData()) {
      const wholesalePercent = Number(target.wholesalePercent);
      if (Number.isFinite(wholesalePercent) && wholesalePercent > 0) {
        percents.add(wholesalePercent);
      }
    }
    if (percents.size === 0) {
      const meta = document.querySelector('meta[name="omalley-cart-wholesale-tier"]');
      const tier = Number(meta?.getAttribute('content') || 15);
      if (Number.isFinite(tier) && tier > 0) {
        percents.add(tier);
      }
    }
    return percents;
  }

  function selectCodeForPercent(wholesalePercent, totalQty) {
    for (const minQty of WHOLESALE_VOLUME_MIN_QTYS) {
      if (totalQty >= minQty) {
        return wholesaleVolumeCode(wholesalePercent, minQty);
      }
    }
    return wholesaleBaseCode(wholesalePercent);
  }

  function selectWholesaleDiscountCode(cart) {
    const totalQty = (cart.items || []).reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0,
    );
    const wholesalePercents = getCartWholesalePercents();

    if (wholesalePercents.size > 1) {
      console.warn(
        "O'Malley cart mixes multiple wholesale rates; checkout can only apply one discount code on Basic Shopify.",
      );
    }

    const primaryPercent = wholesalePercents.size
      ? Math.max(...wholesalePercents)
      : 15;

    return selectCodeForPercent(primaryPercent, totalQty);
  }

  function isWholesaleDiscountCode(code) {
    const normalized = String(code || '').trim().toUpperCase();
    return allKnownWholesaleCodes()
      .map((entry) => entry.toUpperCase())
      .includes(normalized);
  }

  function usesAutomaticWholesaleCheckout() {
    const meta = document.querySelector('meta[name="omalley-wholesale-automatic-checkout"]');
    return meta?.getAttribute('content') === 'true';
  }

  function shouldManageCheckoutPricing() {
    return readWholesaleCustomerFlag() || readPricingData().length > 0;
  }

  function readPricingData() {
    const node = document.getElementById('omalley-cart-checkout-data');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function mergeProperties(existing, next) {
    const merged = { ...(existing || {}) };
    Object.entries(next).forEach(([key, value]) => {
      if (value == null || value === '') {
        delete merged[key];
      } else {
        merged[key] = String(value);
      }
    });
    return merged;
  }

  function buildHiddenLineProperties(existing, desiredPct, desiredValue) {
    const properties = { ...(existing || {}) };
    properties[HIDDEN_PCT_KEY] = desiredPct;
    properties[HIDDEN_PRICING_KEY] = desiredValue;
    delete properties[VISIBLE_PCT_KEY];
    delete properties[VISIBLE_PRICING_KEY];
    return properties;
  }

  function propertyMatches(line, desiredPct, desiredValue) {
    const pctValue = String(
      line.properties?.[HIDDEN_PCT_KEY] || line.properties?.[VISIBLE_PCT_KEY] || '',
    ).trim();
    const pricingValue = String(
      line.properties?.[HIDDEN_PRICING_KEY] || line.properties?.[VISIBLE_PRICING_KEY] || '',
    ).trim();
    const hasNoVisibleKeys =
      line.properties?.[VISIBLE_PCT_KEY] == null && line.properties?.[VISIBLE_PRICING_KEY] == null;
    return pctValue === desiredPct && pricingValue === desiredValue && hasNoVisibleKeys;
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) {
      throw new Error('Could not load cart');
    }
    return response.json();
  }

  async function changeLine(lineKey, quantity, properties) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineKey,
        quantity,
        properties,
      }),
    });
    if (!response.ok) {
      throw new Error('Could not update cart line');
    }
    return response.json();
  }

  async function updateCartAttributes(attributes) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes }),
    });
    if (!response.ok) {
      throw new Error('Could not update cart attributes');
    }
    return response.json();
  }

  function buildCartAttributePayload(targets) {
    return JSON.stringify(
      targets
        .filter((target) => target.variantId && target.pct)
        .map((target) => ({
          variantId: String(target.variantId),
          pct: String(target.pct),
          label: String(target.label || 'Wholesale volume pricing'),
        })),
    );
  }

  function getAppliedDiscountCodes(cart) {
    if (!Array.isArray(cart.discount_codes)) return [];
    return cart.discount_codes
      .map((entry) => String(entry?.code || entry || '').toUpperCase())
      .filter(Boolean);
  }

  function isExpectedDiscountCodeApplied(cart, code) {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return false;
    return getAppliedDiscountCodes(cart).includes(normalized);
  }

  async function updateCartDiscount(code) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: code ?? '' }),
    });
    if (!response.ok) {
      return null;
    }
    return response.json();
  }

  async function applyWholesaleDiscountCode(code) {
    if (!code || applyingDiscount) return null;
    applyingDiscount = true;
    try {
      const cart = await fetchCart();
      const appliedCodes = getAppliedDiscountCodes(cart);
      const normalized = code.toUpperCase();
      const hasStaleWholesaleCode = appliedCodes.some(
        (entry) => isWholesaleDiscountCode(entry) && entry !== normalized,
      );

      if (hasStaleWholesaleCode) {
        await updateCartDiscount('');
      }

      let updated = await updateCartDiscount(code);
      if (updated && isExpectedDiscountCodeApplied(updated, code)) {
        sessionStorage.setItem(DISCOUNT_APPLIED_KEY, code);
        return updated;
      }

      const response = await fetch(
        `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart')}`,
        {
          credentials: 'same-origin',
          redirect: 'follow',
        },
      );
      if (!response.ok) {
        return null;
      }

      updated = await fetchCart();
      if (isExpectedDiscountCodeApplied(updated, code)) {
        sessionStorage.setItem(DISCOUNT_APPLIED_KEY, code);
        return updated;
      }

      return null;
    } finally {
      applyingDiscount = false;
    }
  }

  async function clearWholesaleDiscountCodes() {
    const cart = await fetchCart();
    const appliedCodes = getAppliedDiscountCodes(cart);
    const hasWholesaleCode = appliedCodes.some((entry) => isWholesaleDiscountCode(entry));
    if (!hasWholesaleCode) return cart;
    return (await updateCartDiscount('')) ?? cart;
  }

  async function ensureWholesaleDiscountApplied() {
    if (!readWholesaleCustomerFlag()) {
      return true;
    }

    const cart = await fetchCart();
    if (cart.item_count === 0) {
      return true;
    }

    if (usesAutomaticWholesaleCheckout()) {
      await clearWholesaleDiscountCodes();
      sessionStorage.removeItem(DISCOUNT_APPLIED_KEY);
      return true;
    }

    const code = selectWholesaleDiscountCode(cart);

    if (isExpectedDiscountCodeApplied(cart, code)) {
      sessionStorage.setItem(DISCOUNT_APPLIED_KEY, code);
      return true;
    }

    const updated = await applyWholesaleDiscountCode(code);
    return updated != null && isExpectedDiscountCodeApplied(updated, code);
  }

  function hideAcceleratedCheckout() {
    if (!shouldManageCheckoutPricing()) return;
    document
      .querySelectorAll(
        '.additional-checkout-buttons, .shopify-payment-button, shopify-accelerated-checkout-cart',
      )
      .forEach((node) => {
        node.setAttribute('hidden', 'hidden');
        node.style.display = 'none';
      });
  }

  async function syncOmalleyCartPricing() {
    if (syncing) return false;
    const targets = readPricingData();
    const desiredWholesaleFlag = readWholesaleCustomerFlag() ? 'true' : '';

    syncing = true;
    try {
      const cart = await fetchCart();
      let changed = false;

      for (const target of targets) {
        const line = cart.items.find((entry) => entry.key === target.key);
        if (!line) continue;

        const desiredPct = String(target.pct || '').trim();
        if (!desiredPct) continue;

        const desiredLabel = String(
          target.label || 'Wholesale volume pricing',
        ).trim();
        const desiredValue = JSON.stringify({
          pct: desiredPct,
          label: desiredLabel,
        });

        if (propertyMatches(line, desiredPct, desiredValue)) {
          continue;
        }

        const properties = buildHiddenLineProperties(line.properties, desiredPct, desiredValue);

        await changeLine(line.key, line.quantity, properties);
        changed = true;
      }

      const attributePayload = buildCartAttributePayload(targets);
      const currentAttribute = String(cart.attributes?.[CART_ATTRIBUTE_KEY] || '');
      const currentWholesaleFlag = String(
        cart.attributes?.[WHOLESALE_ATTRIBUTE_KEY] || '',
      );
      const attributeUpdates = {};

      if (attributePayload !== '[]' && currentAttribute !== attributePayload) {
        attributeUpdates[CART_ATTRIBUTE_KEY] = attributePayload;
      }

      if (currentWholesaleFlag !== desiredWholesaleFlag) {
        attributeUpdates[WHOLESALE_ATTRIBUTE_KEY] = desiredWholesaleFlag;
      }

      if (Object.keys(attributeUpdates).length > 0) {
        await updateCartAttributes(attributeUpdates);
        changed = true;
      }

      if (readWholesaleCustomerFlag()) {
        const discountReady = await ensureWholesaleDiscountApplied();
        changed = changed || discountReady;
      }

      return changed;
    } finally {
      syncing = false;
    }
  }

  async function verifyPricingSynced() {
    const targets = readPricingData();
    const propertiesReady =
      targets.length === 0 ||
      (await fetchCart()).items.every((line) => {
        const target = targets.find((entry) => entry.key === line.key);
        if (!target) return true;
        const desiredPct = String(target.pct || '').trim();
        if (!desiredPct) return true;
        const desiredLabel = String(
          target.label || 'Wholesale volume pricing',
        ).trim();
        const desiredValue = JSON.stringify({
          pct: desiredPct,
          label: desiredLabel,
        });
        return propertyMatches(line, desiredPct, desiredValue);
      });

    if (!readWholesaleCustomerFlag()) {
      return propertiesReady;
    }

    if (usesAutomaticWholesaleCheckout()) {
      return propertiesReady;
    }

    const cart = await fetchCart();
    const code = selectWholesaleDiscountCode(cart);
    const discountReady =
      cart.item_count === 0 || isExpectedDiscountCodeApplied(cart, code);

    return propertiesReady && discountReady;
  }

  async function syncUntilVerified(maxAttempts = 8) {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await syncOmalleyCartPricing();
      if (await verifyPricingSynced()) return true;
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
    return false;
  }

  function redirectToCheckoutWithDiscount(code) {
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/checkout')}`;
  }

  function submitCartCheckout() {
    const form =
      document.getElementById('cart') ||
      document.getElementById('CartDrawer-Form');
    if (!(form instanceof HTMLFormElement)) {
      window.location.href = '/checkout';
      return;
    }

    let checkoutInput = form.querySelector('input[name="checkout"]');
    if (!checkoutInput) {
      checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      form.appendChild(checkoutInput);
    }

    form.submit();
  }

  async function goToCheckout(button) {
    const originalText = button?.textContent;
    if (button) {
      button.disabled = true;
      if (originalText) {
        button.textContent = 'Applying pricing…';
      }
    }

    try {
      await syncUntilVerified();
      if (readWholesaleCustomerFlag() && usesAutomaticWholesaleCheckout()) {
        await clearWholesaleDiscountCodes();
        submitCartCheckout();
        return;
      }
      if (readWholesaleCustomerFlag()) {
        const cart = await fetchCart();
        const discountCode = selectWholesaleDiscountCode(cart);
        if (cart.item_count > 0 && !isExpectedDiscountCodeApplied(cart, discountCode)) {
          console.warn("O'Malley checkout pricing sync could not verify cart discounts.");
        }
        if (discountCode) {
          redirectToCheckoutWithDiscount(discountCode);
          return;
        }
      }
    } catch (error) {
      console.warn("O'Malley checkout pricing sync failed:", error);
    }

    submitCartCheckout();
  }

  function bindCheckoutButtons() {
    document.addEventListener(
      'click',
      (event) => {
        if (!shouldManageCheckoutPricing()) return;

        const target = event.target;
        if (!(target instanceof Element)) return;

        const checkoutLink = target.closest('a[href="/checkout"], a[href^="/checkout?"]');
        if (checkoutLink) {
          event.preventDefault();
          event.stopPropagation();
          goToCheckout(undefined);
          return;
        }

        const button = target.closest('[name="checkout"]');
        if (!button) return;

        event.preventDefault();
        event.stopPropagation();
        goToCheckout(button instanceof HTMLButtonElement ? button : undefined);
      },
      true,
    );

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!form.querySelector('[name="checkout"]')) return;
        if (!shouldManageCheckoutPricing()) return;

        event.preventDefault();
        event.stopPropagation();
        const button = form.querySelector('[name="checkout"]');
        goToCheckout(button instanceof HTMLButtonElement ? button : undefined);
      },
      true,
    );
  }

  function init() {
    hideAcceleratedCheckout();
    bindCheckoutButtons();
    syncOmalleyCartPricing().catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('cart:updated', () => {
    hideAcceleratedCheckout();
    syncOmalleyCartPricing().catch(() => {});
  });

  window.OmalleyCartCheckoutSync = {
    syncOmalleyCartPricing,
    goToCheckout,
    ensureWholesaleDiscountApplied,
  };
})();
